import {
    ActionIcon,
    Badge,
    Button,
    Center,
    Group,
    Loader,
    Menu,
    Popover,
    Progress,
    Select,
    Stack,
    Text,
    TextInput,
    Title,
    Tooltip,
} from "@mantine/core";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import { IconChevronDown, IconSearch, IconUserCog, IconX } from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import { MantineReactTable, MRT_ColumnDef, useMantineReactTable } from "mantine-react-table";
import { Suspense, type UIEvent, useCallback, useRef, useState } from "react";
import { Content } from "shared/components/global/Content";
import { CreateUserForm } from "shared/components/organization/users/CreateUserForm";
import { OrganizationRolesRepository, UserSchema, UsersSearchRepository } from "shared/components/organization/users/data";
import { FloatingActionToolbar } from "shared/components/organization/users/FloatingActionToolbar";
import { GroupSearchCombobox } from "shared/components/organization/users/GroupSearchCombobox";
import { RoleAssignModal } from "shared/components/organization/users/RoleAssignModal";
import { RoleEditModal } from "shared/components/organization/users/RoleEditModal";
import { UploadUsersForm } from "shared/components/organization/users/UploadUsersForm";
import { UserDrawer } from "shared/components/organization/users/UserDrawer";
import { getRelativeTime } from "shared/components/RelativeTime";
import { USERS_TABLE_ROW_HEIGHT_PX } from "shared/const";
import { groupsQueryOptions } from "shared/graphql/groups";
import { topicsQueryOptions } from "shared/graphql/topics";
import { useIsMobile } from "shared/hooks/useIsMobile";
import { usePermissionCheck } from "shared/stores/oidc";
import { useAside } from "shared/utils/aside";
import { titlecase, uniqueBy } from "shared/utils/fns";
import { logger } from "shared/utils/logger";
import { maybe } from "shared/utils/maybe";
import { Permission, UserStatus } from "src/gql/graphql";
import { UserStatusSchema } from "src/gql/zod";
import { queryClient } from "src/queryClient";
import { container } from "tsyringe";

const columns: MRT_ColumnDef<UserSchema>[] = [
    {
        accessorKey: "contactInfo",
        header: "Name",
        Cell: ({ row }) => {
            const { name, loginEmail } = row.original;

            return (
                <Tooltip
                    label={
                        <>
                            <Text fz="lg">{!!name?.trim() ? name : "No name"}</Text>
                            {!!loginEmail && <Text fz="sm">{loginEmail}</Text>}
                        </>
                    }
                >
                    <Stack
                        gap={0}
                        h={USERS_TABLE_ROW_HEIGHT_PX}
                        mah={USERS_TABLE_ROW_HEIGHT_PX}
                        style={{ overflow: "hidden", cursor: "auto" }}
                        justify="center"
                    >
                        <Text fz="lg" truncate>
                            {!!name?.trim() ? name : "No name"}
                        </Text>
                        {!!loginEmail && (
                            <Text fz="sm" truncate>
                                {loginEmail}
                            </Text>
                        )}
                    </Stack>
                </Tooltip>
            );
        },
    },
    {
        accessorKey: "roles",
        header: "Roles",
        Cell: ({ cell, row }) => {
            const [popoverOpened, { close: closePopover, toggle: togglePopover }] = useDisclosure(false);
            const [roleModal, setRoleModal] = useState<{ user: UserSchema; role: UserSchema["roles"][0]["role"] }>();

            const roles = cell.getValue<UserSchema["roles"]>().filter(uniqueBy(({ role: { id } }) => id));
            const roleBadges = roles.map((role) => (
                <Badge
                    key={role.role.id + (role.group?.id ?? "") + (role.topic?.id ?? "")}
                    color={!!role.group ? "blue" : !!role.topic ? "green" : "yellow"}
                    component="button"
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                        closePopover();
                        setRoleModal({ user: row.original, role: role.role });
                    }}
                >
                    {role.role.name}
                </Badge>
            ));

            return (
                <Group h={USERS_TABLE_ROW_HEIGHT_PX} mah={USERS_TABLE_ROW_HEIGHT_PX} style={{ overflow: "auto" }}>
                    {!!roleModal && <RoleEditModal user={roleModal.user} role={roleModal.role} onClose={() => setRoleModal(undefined)} />}
                    {roles.length == 0 ? (
                        <Badge
                            color="red"
                            component="button"
                            style={{ cursor: "pointer" }}
                            onClick={() => modals.open({ title: "Assign Role", children: <RoleAssignModal userIds={[row.original.id]} /> })}
                        >
                            None
                        </Badge>
                    ) : roles.length <= 2 ? (
                        roleBadges
                    ) : (
                        <Popover opened={popoverOpened} onChange={togglePopover} trapFocus withArrow shadow="xl">
                            <Popover.Target>
                                <Badge color="gray" component="button" onClick={togglePopover} style={{ cursor: "pointer" }}>
                                    <Group gap="xs">
                                        View All <IconChevronDown size={16} />
                                    </Group>
                                </Badge>
                            </Popover.Target>
                            <Popover.Dropdown>
                                <Group wrap="wrap" w="fit-content" maw={400} justify="center">
                                    {roleBadges}
                                </Group>
                            </Popover.Dropdown>
                        </Popover>
                    )}
                </Group>
            );
        },
    },
    {
        accessorKey: "groups",
        header: "Groups",
        Cell: ({ cell }) => {
            const groups = cell
                .getValue<UserSchema["groups"]>()
                .map((g) => g.name)
                .join(", ");
            return (
                <Center h={USERS_TABLE_ROW_HEIGHT_PX} mah={USERS_TABLE_ROW_HEIGHT_PX} style={{ display: "flex", alignItems: "center" }}>
                    <Text lineClamp={2} style={{ cursor: "auto" }}>
                        {groups}
                    </Text>
                </Center>
            );
        },
    },
    {
        accessorKey: "status",
        header: "Status",
        Cell: ({ row }) => {
            const { status, lastLogin, statusSince } = row.original;
            const ts = new Date(parseInt(status == UserStatus.Active && !!lastLogin ? lastLogin : statusSince));
            return (
                <Tooltip label={ts.toLocaleString()}>
                    <Text style={{ cursor: "auto" }}>
                        {titlecase(status)} ({getRelativeTime(ts)})
                    </Text>
                </Tooltip>
            );
        },
    },
    {
        accessorKey: "detailsButton",
        header: "More",
        size: 70,
        Cell: ({ row }) => {
            const aside = useAside();
            const { id, name } = row.original;

            return (
                <Stack w="100%" align="center">
                    <ActionIcon
                        variant="default"
                        onClick={() =>
                            aside.present({
                                component: (
                                    <Suspense fallback={<Loader />}>
                                        <UserDrawer userId={id} />
                                    </Suspense>
                                ),
                                label: name || "No name",
                            })
                        }
                    >
                        <IconUserCog size={16} />
                    </ActionIcon>
                </Stack>
            );
        },
    },
];

const UsersDataTable = () => {
    const tableContainerRef = useRef<HTMLDivElement>(null);

    const isMobile = useIsMobile();
    const aside = useAside();

    const [userSearch, setUserSearch] = useState("");
    const [debouncedUserSearch] = useDebouncedValue(userSearch, 200);
    const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);
    const [selectedGroup, setSelectedGroup] = useState<{ name: string; id: string } | null | undefined>(undefined);
    const [selectedStatus, setSelectedStatus] = useState<UserStatus | null>(null);

    const rolesRepo = container.resolve(OrganizationRolesRepository);
    const rolesLoader = rolesRepo.get();
    const roles = rolesLoader.useItems();

    const hasGroupManage = usePermissionCheck(Permission.GroupSubgroupManage);
    const hasUserCreate = usePermissionCheck(Permission.CustomerUsersCreate);

    const usersSearchRepo = container.resolve(UsersSearchRepository);
    const usersSearchLoader = usersSearchRepo.get({
        nameQuery: debouncedUserSearch.length > 0 ? debouncedUserSearch : undefined,
        groupId: selectedGroup?.id ?? undefined,
        roleId: selectedRoleId ?? undefined,
        status: selectedStatus ?? undefined,
        groupless: selectedGroup === null,
    });

    const users = usersSearchLoader.useItems();
    const { isLoading, hasNextPage } = usersSearchLoader.useStatus();

    const [selectedUsers, setSelectedUsers] = useState<{ id: string; status: UserSchema["status"] }[]>([]);
    const rowSelection = selectedUsers.reduce((acc, user) => ({ ...acc, [user.id]: true }), {});

    const table = useMantineReactTable({
        columns,
        data: users,
        enableColumnActions: false,
        enableBottomToolbar: false,
        enablePagination: false,
        enableRowSelection: true,
        enableSelectAll: false,
        enableRowVirtualization: true,
        enableSorting: false,
        enableTopToolbar: false,
        getRowId: (row) => row.id,
        mantineTableContainerProps: {
            ref: tableContainerRef,
            style: { height: "70vh", maxHeight: "1900px", flexGrow: 1 },
            onScroll: (event: UIEvent<HTMLDivElement>) => fetchMore(event.target as HTMLDivElement),
        },
        mantineSelectCheckboxProps({ row }) {
            return {
                onClick: () => {
                    setSelectedUsers((prev) => {
                        if (prev.some((it) => it.id == row.id)) return prev.filter((it) => it.id != row.id);
                        else return prev.concat([{ id: row.id, status: row.getValue("status") }]);
                    });
                },
            };
        },
        state: {
            rowSelection,
            isLoading: isLoading && users.length == 0,
        },
        rowVirtualizerOptions: {
            overscan: 5,
        },
    });

    const fetchMore = useCallback(
        (containerRefElement?: HTMLDivElement | null) => {
            if (containerRefElement) {
                const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
                //once the user has scrolled within 400px of the bottom of the table, fetch more data if we can
                if (scrollHeight - scrollTop - clientHeight < 200 && !isLoading && hasNextPage) {
                    usersSearchLoader.loadMore().catch(logger.error);
                }
            }
        },
        [isLoading, hasNextPage],
    );

    return (
        <Content paper style={{ border: "none" }}>
            <Content.Heading>
                <Title order={3} size="h1">
                    Users
                </Title>
            </Content.Heading>
            <Stack gap={0}>
                <Group
                    pb="sm"
                    maw="100%"
                    justify="space-between"
                    align="flex-end"
                    wrap={isMobile ? "wrap" : "nowrap"}
                    style={{ overflowX: "auto", whiteSpace: "nowrap" }}
                >
                    <Group maw="100%" wrap={isMobile ? "wrap" : "nowrap"} style={{ flexShrink: 0 }}>
                        <TextInput
                            leftSection={<IconSearch size={16} />}
                            label="Search Users"
                            placeholder="Enter a name..."
                            onChange={(data) => setUserSearch(data.currentTarget.value)}
                            value={userSearch}
                            rightSection={
                                userSearch.length > 0 ? (
                                    <ActionIcon variant="transparent" onClick={() => setUserSearch("")} aria-label="Clear search">
                                        <IconX size={16} />
                                    </ActionIcon>
                                ) : undefined
                            }
                        />
                        <Select
                            data={roles.map((r) => ({ value: r.id, label: r.name }))}
                            label="Role"
                            placeholder="Any"
                            clearable
                            rightSectionPointerEvents={selectedRoleId == null ? "none" : undefined}
                            onChange={setSelectedRoleId}
                        />
                        {hasGroupManage && (
                            <GroupSearchCombobox selectedGroup={selectedGroup} setSelectedGroup={setSelectedGroup} nullable={true} />
                        )}
                        <Select
                            data={Object.values(UserStatus)
                                .filter((it) => it != UserStatus.OptIn)
                                .map((it) => ({ value: it, label: titlecase(it) }))}
                            label="Status"
                            placeholder="Any"
                            clearable
                            rightSectionPointerEvents={!selectedStatus ? "none" : undefined}
                            onChange={(e) => setSelectedStatus(maybe(e)?.take(UserStatusSchema.parse) ?? null)}
                            w={100}
                        />
                    </Group>
                    {hasUserCreate && (
                        <Menu>
                            <Menu.Target>
                                <Button variant="default" rightSection={<IconChevronDown size={16} />} style={{ flexShrink: 0 }}>
                                    Invite Users
                                </Button>
                            </Menu.Target>
                            <Menu.Dropdown>
                                <Menu.Item
                                    onClick={() =>
                                        aside.present({
                                            label: "Individual invite",
                                            component: (
                                                <Suspense fallback={<Loader />}>
                                                    <CreateUserForm />
                                                </Suspense>
                                            ),
                                        })
                                    }
                                >
                                    Individual
                                </Menu.Item>
                                <Menu.Item
                                    onClick={() =>
                                        aside.present({
                                            label: "Upload List",
                                            component: (
                                                <Suspense fallback={<Loader />}>
                                                    <UploadUsersForm onClose={() => aside.dismiss()} />
                                                </Suspense>
                                            ),
                                        })
                                    }
                                >
                                    Upload List
                                </Menu.Item>
                            </Menu.Dropdown>
                        </Menu>
                    )}
                </Group>
                <MantineReactTable table={table} />
                {isLoading && <Progress value={100} striped animated style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }} />}
                {selectedUsers.length > 0 && <FloatingActionToolbar selectedUsers={selectedUsers} onClear={() => setSelectedUsers([])} />}
            </Stack>
        </Content>
    );
};

/** @public */
export const Route = createFileRoute("/_auth/organization/users")({
    component: UsersDataTable,
    loader: () =>
        Promise.allSettled([
            container.resolve(UsersSearchRepository).get().ensureData(),
            container.resolve(OrganizationRolesRepository).get().ensureData(),
            queryClient.ensureQueryData(groupsQueryOptions.rootGroup),
            queryClient.ensureQueryData(topicsQueryOptions.rootTopic),
        ]),
});
